import * as React from 'react';

import Imgix from 'js/components/Imgix';

import LazyImgix from 'bundles/page/components/shared/LazyImgix';
import useUserAgent from 'bundles/page/hooks/useUserAgent';

const PRODUCT_CARD_IMAGE_DEFAULTS = { height: 180, width: 320, fit: 'crop', quality: '50' };

export type ImageParams = {
  height?: number;
  width?: number;
  fit?: string;
  crop?: string;
  facepad?: number;
  quality?: number;
};

type Props = {
  imageUrl: string;
  imageParams?: ImageParams;
  prioritizeImageLoading?: boolean;
  enableLazyLoading?: boolean;
  enableFluidWidthForLazyImg?: boolean;
  variant?: 'list' | 'grid';
};

const LearningProductCardImage = ({
  imageUrl,
  imageParams,
  prioritizeImageLoading,
  enableLazyLoading,
  enableFluidWidthForLazyImg,
  variant,
}: Props) => {
  const userAgent = useUserAgent();
  const isMobileWeb = userAgent?.isMobileBrowser || userAgent?.isMobile || false;

  // For mobile user agents (or 'list' variant cards on desktop), we use 'clip'
  // fit to avoid excessive cropping, see MERCH-2225
  const shouldUseClipFit = isMobileWeb || variant === 'list';

  const imgixProps = {
    src: imageUrl,
    alt: '',
    maxHeight: imageParams?.height || PRODUCT_CARD_IMAGE_DEFAULTS.height,
    maxWidth: imageParams?.width || PRODUCT_CARD_IMAGE_DEFAULTS.width,
    imgParams: {
      fit: imageParams?.fit || PRODUCT_CARD_IMAGE_DEFAULTS.fit,
      auto: 'format, compress, enhance',
      quality: imageParams?.quality || PRODUCT_CARD_IMAGE_DEFAULTS.quality,
      ...imageParams,
      ...(shouldUseClipFit ? { fit: 'clip' } : {}),
    },
    fetchPriority: prioritizeImageLoading ? 'high' : undefined,
    style: { maxHeight: 'none', maxWidth: 'none', minWidth: '100%', minHeight: '100%' },
  };

  if (enableLazyLoading) {
    return <LazyImgix {...{ ...imgixProps, enableFluidWidth: enableFluidWidthForLazyImg }} />;
  }
  return <Imgix {...imgixProps} />;
};

export default LearningProductCardImage;
